import {
  AddOnFeeCategory,
  AddOnFees,
  ESenderRole,
  FeeCalculationType,
  IContractDetailsResponseData,
  UserProfile,
} from "../api/services/contract/models";

/**
 * Matches an entry of `partiesProfiles` back to its entry in `parties`.
 *
 * Both sides of a key must be present before it is compared: an unregistered
 * profile carries no `userIdentification` and an unregistered party carries a
 * `userId` of null, so a loose `==` comparison pairs any unregistered profile
 * with the first unregistered party of the contract — regardless of role — and
 * the row then shows that other party's amount, fee and status.
 */
export const findPartyForProfile = (
  parties: Partial<UserProfile>[] | undefined,
  profile:
    | { phoneNumber?: string; userIdentification?: number | string }
    | undefined
) =>
  parties?.find(
    (party) =>
      (!!profile?.phoneNumber && party?.phoneNumber === profile.phoneNumber) ||
      (profile?.userIdentification != null &&
        party?.userId != null &&
        String(party.userId) === String(profile.userIdentification))
  );

type ShareLinkContract = Pick<
  IContractDetailsResponseData,
  "id" | "parties" | "partiesProfiles" | "organization"
>;

/** True when a party or profile entry belongs to a company rather than a person. */
const isCompanyEntry = (entry?: Partial<UserProfile>): boolean =>
  !!entry?.isCompany || !!entry?.companyUnifiedNumber;

/**
 * The `childParticipantId` to hand the public app when a business (company)
 * party is paying: the `id` of its `partiesProfiles.CUSTOMER` entry. Empty when
 * the customer is an individual — those are recognised by their own phone
 * number and need no parameter.
 *
 * A company customer's profile comes back sparse — no `companyUnifiedNumber`,
 * and its `id` is the user id rather than the id on the `parties` entry — so the
 * company is detected on whichever side declares it and the two are paired by
 * position within the CUSTOMER role, which is the only key both lists share.
 */
export const getChildParticipantId = (contract?: ShareLinkContract): string => {
  const profiles = contract?.partiesProfiles?.CUSTOMER || [];
  const customerParties = (contract?.parties || []).filter(
    (party) => party?.role === ESenderRole.CUSTOMER
  );

  // Either list can be the one carrying the company flags.
  const flaggedProfileIndex = profiles.findIndex(isCompanyEntry);
  const flaggedPartyIndex = customerParties.findIndex(isCompanyEntry);
  if (flaggedProfileIndex < 0 && flaggedPartyIndex < 0) return "";

  if (flaggedProfileIndex >= 0) {
    const profile = profiles[flaggedProfileIndex];
    if (profile?.id != null) return `${profile.id}`;
  }

  const party = customerParties[flaggedPartyIndex];

  const paired =
    profiles.find(
      (profile) =>
        (!!party?.companyUnifiedNumber &&
          profile?.companyUnifiedNumber === party.companyUnifiedNumber) ||
        (!!party?.phoneNumber && profile?.phoneNumber === party.phoneNumber) ||
        (party?.userId != null &&
          profile?.userIdentification != null &&
          `${profile.userIdentification}` === `${party.userId}`)
    ) ??
    // Same slot of the CUSTOMER role — the lists are built in step, and this is
    // the only correspondence left once a company profile carries no keys.
    (profiles.length === customerParties.length
      ? profiles[flaggedPartyIndex]
      : undefined);

  return paired?.id != null ? `${paired.id}` : "";
};

/**
 * Base URL of the public contract app for the current environment.
 *
 * `REACT_APP_URL` is defined per environment (app-dev / app-stg / app), so it is
 * the authority. Deriving the environment from the portal's own hostname only
 * works when the portal is served as `portal-<env>` — on localhost it silently
 * resolved to production.
 */
const getPublicAppBaseUrl = (): string => {
  const configured = process.env.REACT_APP_URL?.trim();
  if (configured) return configured.replace(/\/+$/, "");

  const [subdomain] = window.location.hostname.split(".");
  const [, envSuffix] = subdomain.split("-");
  return `https://${envSuffix ? `app-${envSuffix}` : "app"}.waffyapp.com`;
};

/**
 * Builds the public contract link that the milestone share button copies.
 *
 * When the paying customer is a business party the link carries its customer
 * profile id as `childParticipantId`. The public app has no phone number to
 * recognise a company user by, so without that parameter the opened contract
 * cannot tell which participant is acting and the payment has no `fromUserId`.
 */
export const buildContractShareLink = (
  contract?: ShareLinkContract
): string => {
  const link = `${getPublicAppBaseUrl()}/contract/${contract?.id || ""}`;
  const childParticipantId = getChildParticipantId(contract);

  return childParticipantId
    ? `${link}?childParticipantId=${encodeURIComponent(childParticipantId)}`
    : link;
};

export const getShipmentFeesTotal = (
  contract?: IContractDetailsResponseData
) => {
  if (!contract?.addonFeesList?.length || contract?.itemPrice == null) return 0;
  const addonShippingFees = contract?.addonFeesList?.filter(
    (fee) => fee.type === AddOnFeeCategory.SHIPPING_FEES
  );
  const total =
    addonShippingFees?.reduce?.((acc: number, addOnFee: AddOnFees) => {
      if (addOnFee.feeType === FeeCalculationType.FLAT) {
        return acc + addOnFee.amount;
      } else if (addOnFee.feeType === FeeCalculationType.PERCENTAGE) {
        return acc + (contract?.itemPrice * addOnFee.amount) / 100;
      }
      return acc;
    }, 0) ??
    contract?.shipment?.fee ??
    0;
  return total;
};
